import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useGameStore } from '@/features/game/store/gameStore';
import { Logo } from '@/components/shared/Logo';
import { Button } from '@/components/ui/button';
import { 
  Minus, Plus, Trophy, Home, Radio, ArrowRightFromLine, LogOut, RotateCcw
} from 'lucide-react';

import { API_BASE_URL } from '@/lib/api/apiClient';
import { playerLifelineService } from '@/features/game/services/lifelineService';
const API_URL = API_BASE_URL || '';

const POINTS_OPTIONS = [200, 400, 600];

// Helper to get full image URL (returns empty string for missing path so img src is always string)
const getImageUrl = (path?: string | null): string => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const base = API_URL || '';
  const p = path.startsWith('/') ? path : `/${path}`;
  return base ? `${base}${p}` : p;
};

export const GameBoardPage = () => {
  const navigate = useNavigate();
  const {
    gameSettings,
    team1,
    team2,
    currentTurn,
    questionCells,
    nextQuestionIsPit,
    setNextQuestionIsPit,
    switchTurn,
    addScore,
    subtractScore,
    setCurrentCell,
    useTeamLifeline,
    reset,
    resetTeamsOnly,
    setGameSettings,
  } = useGameStore();

  const [showEndGameModal, setShowEndGameModal] = useState(false);

  // Redirect if no game settings
  useEffect(() => {
    if (!gameSettings || !team1 || !team2) {
      navigate('/player/game-settings');
    }
  }, [gameSettings, team1, team2, navigate]);

  // إذا الوسائل فارغة (مثلاً بعد تحديث الصفحة) جلبها وعرضها
  useEffect(() => {
    if (!gameSettings || (gameSettings.lifelines && gameSettings.lifelines.length > 0)) return;
    let cancelled = false;
    playerLifelineService.getEnabledLifelines().then((list) => {
      if (!cancelled && gameSettings && list.length > 0) {
        setGameSettings({ ...gameSettings, lifelines: list });
      }
    });
    return () => { cancelled = true; };
  }, [gameSettings]);

  if (!gameSettings || !team1 || !team2) {
    return null;
  }

  // Check if a specific cell is answered
  const isCellAnswered = (categoryId: string, points: number, side: 'left' | 'right') => {
    const cell = questionCells.find(
      (c) => c.categoryId === categoryId && c.points === points && c.side === side
    );
    return cell?.isAnswered || false;
  };

  // Handle clicking on a question cell
  const handleCellClick = (categoryId: string, points: number, side: 'left' | 'right') => {
    const cell = questionCells.find(
      (c) => c.categoryId === categoryId && c.points === points && c.side === side
    );
    if (cell && !cell.isAnswered) {
      setCurrentCell(cell);
      navigate('/player/question');
    }
  };

  // Calculate remaining questions
  const answeredCount = questionCells.filter((c) => c.isAnswered).length;
  const totalCount = questionCells.length;
  const isGameComplete = answeredCount === totalCount;

  // Check if lifeline is used by specific team
  const isLifelineUsedByTeam = (teamId: 'team1' | 'team2', lifelineId: string) => {
    if (teamId === 'team1') {
      return team1.usedLifelines.includes(lifelineId);
    }
    return team2.usedLifelines.includes(lifelineId);
  };

  // الحفرة: تُفعّل قبل الدخول للسؤال
  const pitLifeline = gameSettings?.lifelines?.find((l) => (l as { code?: string }).code === 'THE_PIT');
  const handleActivatePit = (e: React.MouseEvent, teamId: 'team1' | 'team2') => {
    e.stopPropagation();
    if (!pitLifeline || isLifelineUsedByTeam(teamId, pitLifeline.id)) return;
    setNextQuestionIsPit(true);
    useTeamLifeline(teamId, pitLifeline.id);
  };

  // Handle end game
  const handleEndGame = () => {
    setShowEndGameModal(true);
  };

  const handleConfirmEndGame = () => {
    navigate('/player/results');
  };

  const handlePlayAgain = () => {
    resetTeamsOnly();
    setShowEndGameModal(false);
  };

  const handleExitToHome = () => {
    reset();
    navigate('/player');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-100 to-white" dir="rtl">
      {/* Top Header Bar */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-2 px-4 shadow-lg">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          {/* Right - Logo */}
          <Link to="/player" className="flex items-center gap-2">
            <Logo size="sm" />
          </Link>
          
          {/* Center - Turn Indicator */}
          <div className="flex items-center gap-4">
            <div className="bg-white/20 px-4 py-1.5 rounded-full flex items-center gap-2 backdrop-blur-sm">
              <span className="font-bold">دور فريق :</span>
              <span className={`font-bold px-2 py-0.5 rounded ${currentTurn === 'team1' ? 'bg-red-500' : 'bg-green-400'}`}>
                {currentTurn === 'team1' ? team1.name : team2.name}
              </span>
            </div>
            <span className="bg-white text-green-700 px-3 py-1 rounded-full font-bold">
              السؤال رقم : {answeredCount + 1} / {totalCount}
            </span>
          </div>
          
          {/* Left - Controls */}
          <div className="flex items-center gap-2 text-sm">
            <button 
              onClick={handleEndGame}
              className="flex items-center gap-1 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full transition-colors"
            >
              <Radio className="w-4 h-4" />
              <span>انهاء اللعبة</span>
            </button>
            <button 
              onClick={() => navigate('/player/team-setup')}
              className="flex items-center gap-1 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full transition-colors"
            >
              <ArrowRightFromLine className="w-4 h-4" />
              <span>الرجوع للوحة</span>
            </button>
            <button 
              onClick={handleExitToHome}
              className="flex items-center gap-1 bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-full transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>الخروج</span>
            </button>
          </div>
        </div>
      </div>

      {/* الحفرة فقط من هنا (الخارج) - قبل اختيار السؤال */}
      {pitLifeline && (
        <div className="bg-orange-50 border-b border-orange-200 px-4 py-2.5 flex flex-wrap items-center justify-center gap-3">
          <span className="text-orange-800 font-bold text-sm">الحفرة (من هنا فقط قبل اختيار السؤال):</span>
          {currentTurn === 'team1' && !isLifelineUsedByTeam('team1', pitLifeline.id) && !nextQuestionIsPit && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); handleActivatePit(e, 'team1'); }}
              className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-xl text-sm font-bold shadow transition-colors"
            >
              {pitLifeline.iconUrl ? (
                <img src={getImageUrl(pitLifeline.iconUrl)} alt="الحفرة" className="w-5 h-5 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              ) : (
                <span>🕳️</span>
              )}
              تفعيل الحفرة لفريق {team1.name}
            </button>
          )}
          {currentTurn === 'team2' && !isLifelineUsedByTeam('team2', pitLifeline.id) && !nextQuestionIsPit && (
            <button
              type="button"
              onClick={(e) => { e.preventDefault(); handleActivatePit(e, 'team2'); }}
              className="flex items-center gap-1.5 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl text-sm font-bold shadow transition-colors"
            >
              {pitLifeline.iconUrl ? (
                <img src={getImageUrl(pitLifeline.iconUrl)} alt="الحفرة" className="w-5 h-5 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              ) : (
                <span>🕳️</span>
              )}
              تفعيل الحفرة لفريق {team2.name}
            </button>
          )}
          {nextQuestionIsPit && (
            <span className="text-orange-700 font-bold text-sm bg-orange-100 px-3 py-1 rounded-full">الحفرة مفعّلة للسؤال القادم</span>
          )}
          {(isLifelineUsedByTeam('team1', pitLifeline.id) || isLifelineUsedByTeam('team2', pitLifeline.id)) && !nextQuestionIsPit && (
            <span className="text-gray-500 text-sm">الحفرة مستخدمة</span>
          )}
        </div>
      )}

      {/* Main Game Board */}
      <div className="p-4 max-w-7xl mx-auto">
        {/* Categories Grid - مقاسات موحدة، خلفية النقاط أوف وايت، اسم الفئة نص أحمر فقط */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {gameSettings.categories.map((category) => (
            <div key={category.id} className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-200 flex flex-col min-h-[180px]">
              <div className="flex items-stretch flex-1 min-h-[140px]">
                {/* Left Points - خلفية أوف وايت، عرض كافٍ لظهور الأرقام كاملة */}
                <div className="flex flex-col justify-center gap-2 p-2.5 w-[4.5rem] min-w-[4.5rem] flex-shrink-0 bg-stone-50/90 rounded-l-2xl border-r border-gray-100">
                  {POINTS_OPTIONS.map((points) => {
                    const isAnswered = isCellAnswered(category.id, points, 'left');
                    return (
                      <button
                        key={`${category.id}-${points}-left`}
                        onClick={() => handleCellClick(category.id, points, 'left')}
                        disabled={isAnswered}
                        className={`w-full min-w-[3.5rem] h-9 rounded-full font-bold text-sm transition-all flex items-center justify-center ${
                          isAnswered
                            ? 'bg-gray-300 text-gray-500 cursor-default'
                            : 'bg-red-500 text-white hover:bg-red-600 shadow-sm cursor-pointer'
                        }`}
                      >
                        {points}
                      </button>
                    );
                  })}
                </div>

                {/* Center: صورة الفئة */}
                <div className="flex-1 min-h-0 min-w-0 flex flex-col">
                  <div className="flex-1 min-h-0 overflow-hidden">
                    <img
                      src={getImageUrl(category.imageUrl) || (API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg')}
                      alt={category.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = `${API_URL}/Brain.jpeg`;
                      }}
                    />
                  </div>
                </div>

                {/* Right Points - خلفية أوف وايت، عرض كافٍ لظهور الأرقام كاملة */}
                <div className="flex flex-col justify-center gap-2 p-2.5 w-[4.5rem] min-w-[4.5rem] flex-shrink-0 bg-stone-50/90 rounded-r-2xl border-l border-gray-100">
                  {POINTS_OPTIONS.map((points) => {
                    const isAnswered = isCellAnswered(category.id, points, 'right');
                    return (
                      <button
                        key={`${category.id}-${points}-right`}
                        onClick={() => handleCellClick(category.id, points, 'right')}
                        disabled={isAnswered}
                        className={`w-full min-w-[3.5rem] h-9 rounded-full font-bold text-sm transition-all flex items-center justify-center ${
                          isAnswered
                            ? 'bg-gray-300 text-gray-500 cursor-default'
                            : 'bg-red-500 text-white hover:bg-red-600 shadow-sm cursor-pointer'
                        }`}
                      >
                        {points}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* اسم الفئة - نص أحمر بخط جميل وواضح */}
              <div className="text-center py-2.5 px-2 truncate border-t border-gray-100">
                <span className="text-red-600 font-bold text-base md:text-lg" style={{ fontFamily: "'Aref Ruqaa', 'Amiri', serif" }}>
                  {category.name}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Section - Teams */}
        <div className="grid grid-cols-3 gap-3 items-start pt-14">
          {/* Team 1 (Right) */}
          <div className="relative">
            {/* Arrow indicator - bold and visible */}
            {currentTurn === 'team1' && (
              <div className="absolute -top-14 left-1/2 -translate-x-1/2 flex flex-col items-center z-10">
                <div className="bg-red-600 text-white font-black text-base px-5 py-2 rounded-full shadow-xl animate-pulse mb-1.5 ring-4 ring-red-300">
                  🎯 دور الفريق
                </div>
                <div className="flex flex-col items-center animate-bounce">
                  <div className="w-0 h-0 border-l-[20px] border-r-[20px] border-t-[28px] border-l-transparent border-r-transparent border-t-red-600 shadow-2xl" style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))' }}></div>
                </div>
              </div>
            )}
            <div className={`rounded-2xl p-4 shadow-xl border-2 transition-all duration-300 ${
              currentTurn === 'team1' 
                ? 'border-red-400 bg-gradient-to-br from-red-50/90 to-white ring-2 ring-red-200 shadow-red-100/50' 
                : 'border-gray-200 bg-white hover:shadow-lg'
            }`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-bold bg-gradient-to-r from-red-500 to-red-600 px-3 py-1.5 rounded-full text-xs shadow-lg">
                {team1.name}
              </span>
              <span className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded-lg">وسائل المساعدة</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              {/* النقاط + أزرار +/- 100 */}
              <div className="flex items-center gap-2.5 bg-gray-50/80 rounded-xl p-2 border border-gray-100">
                <button
                  type="button"
                  onClick={() => subtractScore('team1', 100)}
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white flex items-center justify-center shadow-lg hover:shadow-xl ring-2 ring-red-200/50 transition-all duration-200 hover:scale-110 active:scale-95"
                >
                  <Minus className="w-5 h-5 stroke-[2.5]" />
                </button>
                <span className="bg-white text-red-600 font-bold text-xl px-4 py-2 rounded-xl min-w-[76px] text-center border-2 border-red-100 shadow-sm">
                  {team1.score}
                </span>
                <button
                  type="button"
                  onClick={() => addScore('team1', 100)}
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white flex items-center justify-center shadow-lg hover:shadow-xl ring-2 ring-green-200/50 transition-all duration-200 hover:scale-110 active:scale-95"
                >
                  <Plus className="w-5 h-5 stroke-[2.5]" />
                </button>
              </div>
              
              {/* الأربع وسائل ظاهرين: المختار (3) للحفرة من الأعلى والباقي من السؤال، غير المختار معطّل */}
              <div className="flex flex-wrap items-center gap-1.5">
                {(gameSettings.lifelines || []).map((lifeline) => {
                  const isPit = (lifeline as { code?: string }).code === 'THE_PIT';
                  const isChosen = (team1.lifelines || []).some((l) => l.id === lifeline.id);
                  const isUsed = isLifelineUsedByTeam('team1', lifeline.id);
                  return (
                    <div
                      key={lifeline.id}
                      title={isChosen ? lifeline.nameAr : `${lifeline.nameAr} (غير مختار)`}
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs ${
                        !isChosen ? 'bg-gray-100 border-gray-200 opacity-50' : isUsed ? 'bg-gray-100 border-gray-200 opacity-60' : isPit ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {lifeline.iconUrl ? (
                        <img src={getImageUrl(lifeline.iconUrl)} alt="" className="w-4 h-4 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : (
                        <span>{isPit ? '🕳️' : '⭐'}</span>
                      )}
                      <span className="text-gray-600 truncate max-w-[52px]">
                        {!isChosen ? 'غير مختار' : isPit ? (nextQuestionIsPit ? 'مفعّلة' : 'من الأعلى') : 'من السؤال'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          </div>

          {/* Center - Logo & أزرار التحكم */}
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="bg-white rounded-2xl p-4 text-center shadow-xl border-2 border-green-100">
              <img 
                src={API_URL ? `${API_URL}/Brain.jpeg` : '/Brain.jpeg'} 
                alt="Logo" 
                className="w-16 h-16 mx-auto rounded-xl object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <p className="text-green-600 font-bold text-sm mt-2" style={{ fontFamily: "'Aref Ruqaa', 'Amiri', serif" }}>
                اعصر مخك
              </p>
            </div>
            
            <div className="flex flex-col gap-3 w-full">
              <button
                type="button"
                onClick={switchTurn}
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-green-500 via-green-600 to-green-600 hover:from-green-600 hover:via-green-700 hover:to-green-700 text-white font-bold text-base shadow-xl hover:shadow-2xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] ring-2 ring-green-300/50"
              >
                تبديل الدور
              </button>
              
              <button
                type="button"
                onClick={isGameComplete ? () => navigate('/player/results') : handleEndGame}
                className={`w-full py-3.5 rounded-2xl font-bold text-base shadow-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2 ${
                  isGameComplete 
                    ? 'bg-gradient-to-r from-green-500 via-green-600 to-green-600 hover:from-green-600 hover:via-green-700 hover:to-green-700 text-white shadow-xl hover:shadow-2xl ring-2 ring-green-300/50'
                    : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                {isGameComplete ? (
                  <><Trophy className="w-5 h-5" /> النتائج</>
                ) : (
                  <><Home className="w-5 h-5" /> إنهاء</>
                )}
              </button>
            </div>
          </div>

          {/* Team 2 (Left) */}
          <div className="relative">
            {/* Arrow indicator - bold and visible */}
            {currentTurn === 'team2' && (
              <div className="absolute -top-14 left-1/2 -translate-x-1/2 flex flex-col items-center z-10">
                <div className="bg-green-600 text-white font-black text-base px-5 py-2 rounded-full shadow-xl animate-pulse mb-1.5 ring-4 ring-green-300">
                  🎯 دور الفريق
                </div>
                <div className="flex flex-col items-center animate-bounce">
                  <div className="w-0 h-0 border-l-[20px] border-r-[20px] border-t-[28px] border-l-transparent border-r-transparent border-t-green-600 shadow-2xl" style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))' }}></div>
                </div>
              </div>
            )}
            <div className={`rounded-2xl p-4 shadow-xl border-2 transition-all duration-300 ${
              currentTurn === 'team2' 
                ? 'border-green-400 bg-gradient-to-br from-green-50/90 to-white ring-2 ring-green-200 shadow-green-100/50' 
                : 'border-gray-200 bg-white hover:shadow-lg'
            }`}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-white font-bold bg-gradient-to-r from-green-500 to-green-600 px-3 py-1.5 rounded-full text-xs shadow-lg">
                {team2.name}
              </span>
              <span className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded-lg">وسائل المساعدة</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              {/* النقاط + أزرار +/- 100 */}
              <div className="flex items-center gap-2.5 bg-gray-50/80 rounded-xl p-2 border border-gray-100">
                <button
                  type="button"
                  onClick={() => subtractScore('team2', 100)}
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white flex items-center justify-center shadow-lg hover:shadow-xl ring-2 ring-red-200/50 transition-all duration-200 hover:scale-110 active:scale-95"
                >
                  <Minus className="w-5 h-5 stroke-[2.5]" />
                </button>
                <span className="bg-white text-green-600 font-bold text-xl px-4 py-2 rounded-xl min-w-[76px] text-center border-2 border-green-100 shadow-sm">
                  {team2.score}
                </span>
                <button
                  type="button"
                  onClick={() => addScore('team2', 100)}
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white flex items-center justify-center shadow-lg hover:shadow-xl ring-2 ring-green-200/50 transition-all duration-200 hover:scale-110 active:scale-95"
                >
                  <Plus className="w-5 h-5 stroke-[2.5]" />
                </button>
              </div>
              
              {/* الأربع وسائل ظاهرين: المختار (3) للحفرة من الأعلى والباقي من السؤال، غير المختار معطّل */}
              <div className="flex flex-wrap items-center gap-1.5">
                {(gameSettings.lifelines || []).map((lifeline) => {
                  const isPit = (lifeline as { code?: string }).code === 'THE_PIT';
                  const isChosen = (team2.lifelines || []).some((l) => l.id === lifeline.id);
                  const isUsed = isLifelineUsedByTeam('team2', lifeline.id);
                  return (
                    <div
                      key={lifeline.id}
                      title={isChosen ? lifeline.nameAr : `${lifeline.nameAr} (غير مختار)`}
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs ${
                        !isChosen ? 'bg-gray-100 border-gray-200 opacity-50' : isUsed ? 'bg-gray-100 border-gray-200 opacity-60' : isPit ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {lifeline.iconUrl ? (
                        <img src={getImageUrl(lifeline.iconUrl)} alt="" className="w-4 h-4 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : (
                        <span>{isPit ? '🕳️' : '⭐'}</span>
                      )}
                      <span className="text-gray-600 truncate max-w-[52px]">
                        {!isChosen ? 'غير مختار' : isPit ? (nextQuestionIsPit ? 'مفعّلة' : 'من الأعلى') : 'من السؤال'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* End Game Modal */}
      {showEndGameModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center space-y-6">
            <h2 className="text-2xl font-bold text-gray-800">هل تريد إنهاء اللعبة؟</h2>
            
            {/* Current Scores */}
            <div className="flex justify-center gap-4">
              <div className="bg-red-100 rounded-xl p-4 flex-1">
                <p className="text-red-600 font-bold">{team1.name}</p>
                <p className="text-3xl font-bold text-red-700">{team1.score}</p>
              </div>
              <div className="bg-green-100 rounded-xl p-4 flex-1">
                <p className="text-green-600 font-bold">{team2.name}</p>
                <p className="text-3xl font-bold text-green-700">{team2.score}</p>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <Button
                onClick={handleConfirmEndGame}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trophy className="w-5 h-5 ml-2" />
                عرض النتائج النهائية
              </Button>
              <Button
                onClick={handlePlayAgain}
                variant="outline"
                className="border-2 border-green-600 text-green-600"
              >
                <RotateCcw className="w-5 h-5 ml-2" />
                إعادة اللعبة
              </Button>
              <Button
                onClick={handleExitToHome}
                variant="ghost"
                className="text-gray-500"
              >
                الخروج للرئيسية
              </Button>
              <Button
                onClick={() => setShowEndGameModal(false)}
                variant="ghost"
                className="text-gray-400"
              >
                متابعة اللعب
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
